import { TEMPLATE_ID_DUAL_SHARE_CLASS } from './templateConstants';

type DynamicPreiseField = { isDynamic: true; fields: any[] };

function isDynamicPreiseField(value: unknown): value is DynamicPreiseField {
  return typeof value === 'object' && value !== null && (value as DynamicPreiseField).isDynamic === true;
}

function hasMeaningfulPreiseField(value: unknown): boolean {
  if (value === undefined || value === null || value === '') return false;
  if (isDynamicPreiseField(value)) return Array.isArray(value.fields) && value.fields.length > 0;
  return true;
}

function normalizeEurSuffix(text: string): string {
  const trimmed = text.replace(/\s*\/\s*$/, '').trim();
  if (!trimmed) return ' EUR';
  if (/EUR/i.test(trimmed)) {
    return trimmed.replace(/\s*(EUR\s*)+$/gi, ' EUR').replace(/\s+/g, ' ').trim();
  }
  return `${trimmed} EUR`.replace(/\s+/g, ' ').trim();
}

function buildDynamicField(fields: any[]): DynamicPreiseField {
  return { isDynamic: true, fields };
}

function splitDynamicMepreisFields(fields: any[]): { ausgabepreis?: DynamicPreiseField; ruecknahmepreis?: DynamicPreiseField } {
  const ausgabeIdx = fields.findIndex((f: any) => f.type === 'table' && f.field === 'ausgabepreis');
  const ruecknahmeIdx = fields.findIndex((f: any) => f.type === 'table' && f.field === 'ruecknahmepreis');

  if (ausgabeIdx >= 0 && ruecknahmeIdx >= 0) {
    const ausgabeTable = fields[ausgabeIdx];
    const ruecknahmeTable = fields[ruecknahmeIdx];
    const ausgabeTextFields = fields.slice(ausgabeIdx + 1, ruecknahmeIdx).filter((f: any) => f.type === 'text');
    const ruecknahmeTextFields = fields.slice(ruecknahmeIdx + 1).filter((f: any) => f.type === 'text');

    const ausgabeSuffix =
      ausgabeTextFields.length > 0
        ? ausgabeTextFields.map((f: any) =>
            f.type === 'text' ? { ...f, text: normalizeEurSuffix(String(f.text ?? '')) } : f,
          )
        : [{ type: 'text', text: ' EUR' }];

    const ruecknahmeSuffix =
      ruecknahmeTextFields.length > 0
        ? ruecknahmeTextFields.map((f: any) =>
            f.type === 'text' ? { ...f, text: normalizeEurSuffix(String(f.text ?? '')) } : f,
          )
        : [{ type: 'text', text: ' EUR' }];

    return {
      ausgabepreis: buildDynamicField([ausgabeTable, ...ausgabeSuffix]),
      ruecknahmepreis: buildDynamicField([ruecknahmeTable, ...ruecknahmeSuffix]),
    };
  }

  const tableFields = fields.filter((f: any) => f.type === 'table');
  if (tableFields.length >= 2) {
    return {
      ausgabepreis: buildDynamicField([tableFields[0], { type: 'text', text: ' EUR' }]),
      ruecknahmepreis: buildDynamicField([tableFields[1], { type: 'text', text: ' EUR' }]),
    };
  }

  return {};
}

function cloneAusgabeDynamicAsRuecknahme(ausgabepreis: DynamicPreiseField): DynamicPreiseField {
  const clonedFields = (ausgabepreis.fields || []).map((f: any) => {
    if (f.type === 'table' && f.field === 'ausgabepreis') {
      return { ...f, field: 'ruecknahmepreis' };
    }
    if (f.type === 'text') {
      return { ...f, text: normalizeEurSuffix(String(f.text ?? '')) };
    }
    return f;
  });
  return buildDynamicField(clonedFields);
}

/** Ensure ruecknahmepreis exists when only ausgabepreis was migrated/saved. */
export function ensureRuecknahmepreisField(preise: Record<string, unknown>): Record<string, unknown> {
  if (hasMeaningfulPreiseField(preise.ruecknahmepreis)) return preise;
  if (isDynamicPreiseField(preise.ausgabepreis)) {
    return {
      ...preise,
      ruecknahmepreis: cloneAusgabeDynamicAsRuecknahme(preise.ausgabepreis),
    };
  }
  return preise;
}

/** Split legacy combined mepreis into ausgabepreis + ruecknahmepreis (dual share-class). */
export function splitMepreisInPreise(preise: Record<string, unknown> | undefined): Record<string, unknown> {
  if (!preise) return {};

  const hasAusgabe = hasMeaningfulPreiseField(preise.ausgabepreis);
  const hasRuecknahme = hasMeaningfulPreiseField(preise.ruecknahmepreis);
  if (hasAusgabe && hasRuecknahme) {
    return ensureRuecknahmepreisField(preise);
  }

  const mepreis = preise.mepreis;
  let next: Record<string, unknown> = { ...preise };

  if (mepreis !== undefined && mepreis !== null && mepreis !== '') {
    if (isDynamicPreiseField(mepreis)) {
      const split = splitDynamicMepreisFields(mepreis.fields || []);
      if (!hasAusgabe && split.ausgabepreis) next.ausgabepreis = split.ausgabepreis;
      if (!hasRuecknahme && split.ruecknahmepreis) next.ruecknahmepreis = split.ruecknahmepreis;
    } else if (typeof mepreis === 'string') {
      const slashIdx = mepreis.indexOf('/');
      if (slashIdx >= 0) {
        if (!hasAusgabe) next.ausgabepreis = mepreis.slice(0, slashIdx).trim();
        if (!hasRuecknahme) next.ruecknahmepreis = mepreis.slice(slashIdx + 1).trim();
      }
    }
  }

  next = ensureRuecknahmepreisField(next);
  return next;
}

export function applyDualSharePreiseSplit(
  preise: Record<string, unknown> | undefined,
  templateId?: string,
): Record<string, unknown> {
  if (templateId !== TEMPLATE_ID_DUAL_SHARE_CLASS) return preise || {};
  return splitMepreisInPreise(preise);
}

export const DUAL_SHARE_PREISE_ROW_KEYS = [
  ['wahrung', 'Währung:'],
  ['ausgabepreis', 'Ausgabepreis:'],
  ['ruecknahmepreis', 'Rücknahmepreis:'],
  ['fondsvolumen', 'Fondsvolumen:'],
  ['ertragsart', 'Ertragsart:'],
  ['Letzte Ausschüttung', 'Letzte Ausschüttung:'],
  ['ausschuttung', 'Ausschüttungstermin:'],
] as const;

export function isDualSharePriceField(key: string): boolean {
  return key === 'ausgabepreis' || key === 'ruecknahmepreis';
}

export function formatDualSharePriceValue(raw: unknown): string {
  if (raw === undefined || raw === null || raw === '') return '';
  let text = typeof raw === 'string' ? raw.trim() : String(raw).replace(/\./g, ',');
  text = text.replace(/\s*(EUR\s*)+$/gi, ' EUR').replace(/\s+/g, ' ').trim();
  if (!/EUR/i.test(text)) {
    text = `${text} EUR`;
  }
  return text;
}

/** Resolve display value for split price rows; falls back to legacy combined mepreis. */
export function resolveDualSharePriceRaw(
  field: 'ausgabepreis' | 'ruecknahmepreis',
  resolvedDirect: unknown,
  resolvedCombined?: unknown,
): unknown {
  if (resolvedDirect !== undefined && resolvedDirect !== null && String(resolvedDirect).trim() !== '') {
    return resolvedDirect;
  }
  if (typeof resolvedCombined === 'string' && resolvedCombined.includes('/')) {
    const parts = resolvedCombined.split('/').map((part) => part.trim());
    return field === 'ausgabepreis' ? parts[0] : parts[1] || '';
  }
  return resolvedDirect;
}
